import api from './api';

// A permissioned pipeline agent (the Doc/contract reviewer today; marketing / jira later).
export interface Agent {
  key: string;
  name: string;
  persona?: string;
  description?: string;
  doc_type?: string;
  stages?: string[];
  status?: string;
  builtin?: boolean;
  can_configure?: boolean;
  // Full config (only returned by getAgent):
  base_prompt?: string;
  params?: Record<string, any>;
  guardrails?: Record<string, any>;
}

export interface AgentAccessUser {
  id: string;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  allowed_agents: string[];
  effective_agents: string[];
}

export interface AgentAccessResponse {
  users: AgentAccessUser[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AgentConfigUpdate {
  base_prompt?: string;
  params?: Record<string, any>;
  guardrails?: Record<string, any>;
  persona?: string;
  description?: string;
  status?: string;
}

class AgentsService {
  // The agents the current user may use (drives the sidebar). Admins get all.
  async listMyAgents(): Promise<{ agents: Agent[]; is_admin: boolean }> {
    return (await api.get('/agents')).data;
  }

  async getAgent(key: string): Promise<{ agent: Agent }> {
    return (await api.get(`/agents/${key}`)).data;
  }

  async updateAgentConfig(key: string, update: AgentConfigUpdate): Promise<{ agent: Agent }> {
    return (await api.put(`/agents/${key}/config`, update)).data;
  }

  // ---- admin: agent-access management ----
  async listAllAgents(): Promise<{ agents: Agent[] }> {
    return (await api.get('/admin/agents')).data;
  }

  async listAgentAccess(page = 1, per_page = 50, search?: string): Promise<AgentAccessResponse> {
    const params = new URLSearchParams({ page: String(page), per_page: String(per_page) });
    if (search) params.append('search', search);
    return (await api.get(`/admin/agents/access?${params}`)).data;
  }

  async grantAgent(userId: string, agentKey: string): Promise<any> {
    return (await api.post(`/admin/users/${userId}/agents`, { agent_key: agentKey })).data;
  }

  async revokeAgent(userId: string, agentKey: string): Promise<any> {
    return (await api.delete(`/admin/users/${userId}/agents/${agentKey}`)).data;
  }
}

export const agentsService = new AgentsService();
export default agentsService;
